import {
    defaultBodyText,
    defaultDivWrapper,
    defaultHeader,
    errorRedText,
    standardPrimaryButton,
    standardPrimaryButtonOnMouseEnter,
    standardPrimaryButtonOnMouseLeave,
    standardPrimaryLightButton,
    standardPrimaryLightButtonOnMouseEnter,
    standardPrimaryLightButtonOnMouseLeave,
    underlinedBoldBodyText,
} from '@/src/card-linking/components/iframeStyles'
import { useEffect, useState } from 'react'
import { CardPreview } from '@/src/card-linking/components/CardPreview'
import { UseMutationResult } from 'react-query'
import { gradientTextStyles } from '@/src/card-linking/components/colors'
import { convertToTitleCase } from '@/src/common/utilities/convertToTitleCase'

type TRemoveOrAddStep = {
    handleShowPaymentMethodForm: Function
    enrolledCardsData: any
    deleteCard: UseMutationResult<any, any, any, any>
    cardToCardUuid: string
    setAdjustTextForNonActivate: Function
    refetchGift?: Function
}

const RemoveOrAddCardStep = ({
    handleShowPaymentMethodForm,
    enrolledCardsData,
    deleteCard,
    cardToCardUuid,
    setAdjustTextForNonActivate,
    refetchGift = Function,
}: TRemoveOrAddStep) => {
    const hasEnrolledCards: boolean = !!enrolledCardsData?.length
    const [isDeleting, setIsDeleting] = useState<boolean>(false)
    const [selectedCard, setSelectedCard] = useState<string>('')
    const [isSuccess, setIsSuccess] = useState<boolean>(false)
    const selectedCardData = enrolledCardsData?.find((card: any) => {
        return card.id === selectedCard
    })

    const capitalizedBrand: string = convertToTitleCase(selectedCardData?.brand)
    const cardDeleteText = selectedCardData?.card_last_four
        ? `${capitalizedBrand} card ending in ${selectedCardData?.card_last_four}`
        : 'saved card'

    const {
        mutate: deleteSavedCard,
        isLoading: deleteIsLoading,
        isError: deleteIsError,
        isSuccess: deleteIsSuccess,
    } = deleteCard

    // sets the default card for delete
    useEffect(() => {
        if (!selectedCard && hasEnrolledCards) {
            setSelectedCard(enrolledCardsData[0].id)
        }
    }, [enrolledCardsData, hasEnrolledCards, selectedCard])

    useEffect(() => {
        setIsSuccess(deleteIsSuccess)
    }, [deleteIsSuccess])

    if (isSuccess) {
        return (
            <div
                style={{
                    ...defaultDivWrapper,
                    display: 'flex',
                    flexDirection: 'column',
                }}
            >
                <h2 data-testid={'modal-header'} style={defaultHeader}>
                    Delete Activation Card
                </h2>
                <p style={{ ...defaultBodyText, marginBottom: 0 }}>
                    <span style={gradientTextStyles}>Success!</span> Your{' '}
                    {cardDeleteText} has been deleted.
                </p>
                <p style={{ ...defaultBodyText }}>
                    If you deleted a card that a gift was activated to, please
                    return to the gift to activate it to a new debit/credit card
                    or to select another redemption method.
                </p>
            </div>
        )
    }

    if (selectedCardData && isDeleting) {
        return (
            <div
                style={{
                    ...defaultDivWrapper,
                    display: 'flex',
                    flexDirection: 'column',
                }}
            >
                <h2 data-testid={'modal-header'} style={defaultHeader}>
                    Delete Activation Card
                </h2>

                <p style={{ ...defaultBodyText, marginBottom: 0 }}>
                    Deleting will affect all gifts activated to this card.
                </p>
                <p style={defaultBodyText}>
                    You will not receive credit for future purchase made on this
                    card. You can add a new card to be used for activation at
                    any time.
                </p>
                <div style={{ display: 'flex' }} key={selectedCardData.id}>
                    <CardPreview
                        cardId={selectedCardData.id}
                        cardNetwork={selectedCardData.brand}
                        cardLastFour={selectedCardData.card_last_four}
                    />
                </div>
                <button
                    type="submit"
                    style={{
                        ...standardPrimaryButton,
                        marginTop: 'auto',
                    }}
                    onMouseEnter={(e) => standardPrimaryButtonOnMouseEnter(e)}
                    onMouseLeave={(e) => standardPrimaryButtonOnMouseLeave(e)}
                    disabled={deleteIsLoading}
                    onClick={() => {
                        deleteSavedCard(
                            {
                                card_uuid: cardToCardUuid,
                                payment_card_id: selectedCardData.id,
                            },
                            {
                                onSuccess: () => {
                                    setIsDeleting(false)
                                    refetchGift()
                                },
                                onError: (e: any) => {
                                    console.error(e)
                                },
                            }
                        )
                    }}
                >
                    {deleteIsLoading ? 'Loading...' : 'Delete Saved Card'}
                </button>
                <button
                    type="button"
                    style={{ ...standardPrimaryLightButton, marginTop: 0 }}
                    onMouseEnter={(e) =>
                        standardPrimaryLightButtonOnMouseEnter(e)
                    }
                    onMouseLeave={(e) =>
                        standardPrimaryLightButtonOnMouseLeave(e)
                    }
                    onClick={() => setIsDeleting(false)}
                    disabled={deleteIsLoading}
                >
                    Cancel
                </button>
                {deleteIsError && (
                    <p style={errorRedText}>
                        An unexpected error occurred. Please try again or click
                        Chat Now for assistance.
                    </p>
                )}
            </div>
        )
    }

    return (
        <div
            style={{
                ...defaultDivWrapper,
                display: 'flex',
                flexDirection: 'column',
            }}
        >
            <h2 data-testid={'modal-header'} style={defaultHeader}>
                My Activation Methods
            </h2>
            <p style={{ ...defaultBodyText, marginBottom: 20 }}>
                When you receive a gift, these are the cards available when
                activating gifts.
            </p>
            {hasEnrolledCards && (
                <>
                    {enrolledCardsData.map((card: any) => {
                        return (
                            <div style={{ display: 'flex' }} key={card.id}>
                                <input
                                    type="radio"
                                    id={card.id}
                                    name={'payment'}
                                    onChange={() => setSelectedCard(card.id)}
                                    style={{ cursor: 'pointer' }}
                                    checked={selectedCard === card.id}
                                />
                                <CardPreview
                                    cardId={card.id}
                                    cardNetwork={card.brand}
                                    cardLastFour={card.card_last_four}
                                />
                            </div>
                        )
                    })}
                    <button
                        type="submit"
                        style={{ ...standardPrimaryButton }}
                        onMouseEnter={(e) =>
                            standardPrimaryButtonOnMouseEnter(e)
                        }
                        onMouseLeave={(e) =>
                            standardPrimaryButtonOnMouseLeave(e)
                        }
                        onClick={() => {
                            setIsDeleting(true)
                            setIsSuccess(false)
                        }}
                    >
                        Delete Saved Card
                    </button>
                </>
            )}
            <button
                style={{
                    ...underlinedBoldBodyText,
                    marginTop: 15,
                    textAlign: 'left',
                }}
                onClick={(e) => {
                    e.preventDefault()
                    setIsSuccess(false)
                    setAdjustTextForNonActivate(true)
                    handleShowPaymentMethodForm()
                }}
            >
                Add new credit or debit card
            </button>
        </div>
    )
}

export default RemoveOrAddCardStep
